/* Product 3D models with finishes: show the Format the shopper picked.
 *
 * A framed wall-art model from ~/Projects/3d-gen (`bin/frame3d --finishes all`) is ONE GLB holding
 * every Format as a KHR_materials_variants variant: twelve frame finishes, Art Only, Canvas and
 * Metal Print, with the black frame as the default. Each variant is named by the slug of its
 * Format string, so this script needs no list of finishes:
 *
 *   "Framed - Black & Gold"   -> black-gold
 *   "Framed - Natural Wood"   -> natural-wood
 *   "Art Only (no frame)"     -> art-only
 *
 * (lower-case, drop "Framed -" and any parenthetical, words joined by hyphens: the same rule as
 * slugify() in 3d-gen's scripts/frame3d.py). When the selected product variant has an option
 * value whose slug is one of the model's variants, every <model-viewer> on the page showing that
 * model switches to it; a Format the model doesn't have shows the default.
 *
 * Models without variants (every book) are never touched. The camera is never moved
 * (pd-3d-camera.js owns it). It works whichever app draws the gallery (pmslider creates and
 * recreates <model-viewer> late) and whichever picker sets the variant: it listens for GemPages'
 * gp:product-change-variant event, any change event, and history navigation, and checks the cart
 * form's variant id and the URL's ?variant= once a second while a model with variants is on the page.
 *
 * AR at the chosen size and Format. AR places a model at its own size, and Quick Look can't switch
 * variants, so a framed print has AR files per size (3d-gen scripts/frame_ar.py, uploaded to Shopify
 * Files by scripts/shopify_files.py): per size, one GLB with every Format as a variant, and one USDZ per
 * Format. Their URLs are in a manifest in Shopify Files named by the product id,
 * /cdn/shop/files/pd-ar-<product id>.json. pmslider's full-screen viewer is the only one with `ar`
 * (ar-modes "webxr scene-viewer quick-look", no ios-src); when that viewer shows a model with variants,
 * this script fetches the manifest (once; nothing at page load, and never for a model without
 * variants, so books don't request it) and then, for the selected Size and Format:
 *   - `src` = that size's GLB: Android's WebXR places the scene at scale 1 (model-viewer ignores
 *     `scale` there), and Scene Viewer opens `src` (its default black frame: it can't switch variants);
 *   - `ios-src` = that size and Format's USDZ, which Quick Look opens instead of the USDZ model-viewer
 *     would otherwise build in the browser from the 16.5 x 30 model;
 *   - a one-line label in the viewer: "In your space: 23 × 42 in, Black frame" ("Shown at …" where
 *     the device has no AR).
 * A product without a manifest (a 404), or a size or Format the manifest lacks, keeps pmslider's own
 * src and no ios-src. The gallery's inline viewer (no `ar`) is never given the AR files.
 *
 * Included from layout/theme.liquid just before </body> (see storefront_theme/README.md).
 */
(function () {
  // pmslider's full-screen viewer puts `pmslider-modal-open` on <body>. While it is open, the
  // Help/nudge bar (#pediment-chat) and a Courier pop-up would sit on top of the viewer's AR button
  // and size label, so they step aside and come back when the viewer closes.
  var quiet = document.createElement('style');
  quiet.textContent = 'body.pmslider-modal-open #pediment-chat,' +
    'body.pmslider-modal-open em-popup-drawer{display:none!important}';
  document.head.appendChild(quiet);

  // Format slugs whose model variant has a different name. Empty: 3d-gen names variants by the
  // same rule. Add 'format-slug': 'variant-name' if a store Format is renamed but the model isn't.
  var OVERRIDES = {};

  // Variants that hide the frame. model-viewer draws its floor shadow from the whole model, the
  // hidden frame included, which would leave a frame-wide shadow floating below the bare print, so
  // the shadow is off while one of these shows and restored afterwards.
  var NO_SHADOW = { 'art-only': 1, 'canvas': 1, 'metal-print': 1 };

  var product = null;        // /products/<handle>.js: { variants: [{ id, options, available }] }
  var productRequest = null;
  var selectedId = null;     // the variant id this script last applied, as a string
  var polling = false;
  var manifest;              // AR manifest: undefined (not asked), null (none or failed), or the object
  var manifestRequest = null;

  function slug(value) {
    return String(value).toLowerCase()
      .replace(/\([^)]*\)/g, ' ')
      .replace(/^\s*framed\s*[-:]?\s*/, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function variantsOf(mv) {
    return mv.loaded && mv.availableVariants && mv.availableVariants.length ? mv.availableVariants : null;
  }

  function loadProduct() {
    if (productRequest) return productRequest;
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m || !window.fetch) return (productRequest = Promise.resolve(null));
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    productRequest = fetch(root + 'products/' + m[1] + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { product = p && p.variants ? p : null; return product; })
      .catch(function () { return null; });
    return productRequest;
  }

  function findVariant(id) {
    if (!product || id == null) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(id)) return product.variants[i];
    }
    return null;
  }

  // The selected variant: the cart form's id (what Add to cart would buy), else ?variant=, else
  // the first available variant (what Shopify selects by default). Ids of other products on the
  // page (cross-sells, upsells) are ignored.
  function selectedVariant() {
    var inputs = document.querySelectorAll('form[action*="/cart/add"] [name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      var v = findVariant(inputs[i].value);
      if (v) return v;
    }
    var fromUrl = findVariant(new URLSearchParams(location.search).get('variant'));
    if (fromUrl) return fromUrl;
    if (!product) return null;
    for (var k = 0; k < product.variants.length; k++) if (product.variants[k].available) return product.variants[k];
    return product.variants[0] || null;
  }

  function wantedName(names, variant) {
    var options = (variant && variant.options) || [];
    for (var i = 0; i < options.length; i++) {
      var s = slug(options[i]);
      s = OVERRIDES[s] || s;
      if (names.indexOf(s) !== -1) return s;
    }
    return null;              // the model's default (the black frame)
  }

  function setShadow(mv, name) {
    if (name && NO_SHADOW[name]) {
      if (mv.dataset.pdShadow == null) mv.dataset.pdShadow = mv.getAttribute('shadow-intensity') || '';
      mv.setAttribute('shadow-intensity', '0');
    } else if (mv.dataset.pdShadow != null) {
      if (mv.dataset.pdShadow) mv.setAttribute('shadow-intensity', mv.dataset.pdShadow);
      else mv.removeAttribute('shadow-intensity');
      delete mv.dataset.pdShadow;
    }
  }

  // ---- AR at the chosen size and Format (the full-screen viewer only)
  function loadManifest() {
    if (manifestRequest) return manifestRequest;
    if (!product || !window.fetch) return Promise.resolve(null);
    // Hourly cache key: the CDN keeps a file for a year, and the manifest's name never changes.
    var url = '/cdn/shop/files/pd-ar-' + product.id + '.json?v=' + Math.floor(Date.now() / 3600000);
    manifestRequest = fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (m) { manifest = m && m.sizes ? m : null; return manifest; })
      .catch(function () { manifest = null; return null; });
    return manifestRequest;
  }

  function arSize(variant) {
    var options = (variant && variant.options) || [];
    for (var i = 0; i < options.length; i++) if (manifest.sizes[options[i]]) return options[i];
    return null;
  }

  function formatLabel(variant, name) {
    var options = (variant && variant.options) || [];
    for (var i = 0; i < options.length; i++) {
      if (slug(options[i]) !== name) continue;
      var raw = String(options[i]).replace(/\([^)]*\)/g, '').trim();
      var framed = raw.match(/^\s*framed\s*[-:]?\s*(.+)$/i);
      if (framed) return framed[1].trim() + ' frame';
      return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();    // Art only, Canvas, Metal print
    }
    return '';
  }

  var LABEL_CSS = 'position:absolute;left:12px;bottom:16px;max-width:calc(100% - 84px);box-sizing:border-box;' +
    'padding:5px 10px;border-radius:999px;background:rgba(255,255,255,.88);color:#37352F;' +
    'font:500 12px/1.3 -apple-system,BlinkMacSystemFont,"Segoe UI",Inter,Helvetica,Arial,sans-serif;' +
    'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:none;z-index:2;' +
    'box-shadow:0 1px 3px rgba(0,0,0,.12)';

  // texts: the label from longest to shortest; the first that fits on one line is shown (the last one
  // ellipsises if even it doesn't).
  function setLabel(mv, texts) {
    var el = mv.querySelector(':scope > .pd-ar-size');
    if (!texts) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement('div');
      el.className = 'pd-ar-size';
      el.setAttribute('style', LABEL_CSS);
      el.setAttribute('aria-live', 'polite');
      mv.appendChild(el);
    }
    for (var i = 0; i < texts.length; i++) {
      el.textContent = texts[i];
      if (el.scrollWidth <= el.clientWidth + 1) break;
    }
  }

  function clearAR(mv) {
    if (mv.dataset.pdArSrc) {
      if (mv.getAttribute('src') === mv.dataset.pdArSrc && mv.dataset.pdOrigSrc) mv.setAttribute('src', mv.dataset.pdOrigSrc);
      delete mv.dataset.pdArSrc;
      delete mv.dataset.pdOrigSrc;
    }
    if (mv.dataset.pdIosSrc) { mv.removeAttribute('ios-src'); delete mv.dataset.pdIosSrc; }
    setLabel(mv, null);
  }

  function applyAR(mv, variant, name) {
    if (!mv.hasAttribute('ar')) return;
    if (manifest === undefined) {          // first AR viewer with variants: ask once, then apply
      loadManifest().then(function () { if (manifest) applyAR(mv, selectedVariant() || variant, name); });
      return;
    }
    var size = manifest && arSize(variant);
    var entry = size && manifest.sizes[size];
    var usdz = entry && name && entry.usdz && entry.usdz[name];
    if (!entry || !usdz) { clearAR(mv); return; }
    if (mv.getAttribute('src') !== entry.glb) {
      if (!mv.dataset.pdArSrc) mv.dataset.pdOrigSrc = mv.getAttribute('src') || '';
      mv.dataset.pdArSrc = entry.glb;
      mv.setAttribute('src', entry.glb);            // reloads; the load event re-applies the variant
    }
    if (mv.getAttribute('ios-src') !== usdz) { mv.setAttribute('ios-src', usdz); mv.dataset.pdIosSrc = usdz; }
    var sizeText = size.replace(/\s*x\s*/i, ' \u00d7 ') + ' in';
    var what = formatLabel(variant, name), bare = what.replace(/ frame$/, '');
    var lead = mv.canActivateAR ? 'In your space: ' : 'Shown at ';
    setLabel(mv, what ? [lead + sizeText + ', ' + what, lead + sizeText + ', ' + bare, sizeText + ', ' + bare]
                      : [lead + sizeText]);
    // model-viewer works out whether AR can open a moment after a load, with no event for it:
    // label again once it has ("In your space" only where the AR button shows).
    if (!mv.canActivateAR && !mv.dataset.pdArRecheck) {
      mv.dataset.pdArRecheck = '1';
      setTimeout(function () { var v = selectedVariant(); if (v) apply(mv, v); }, 1200);
    }
  }

  // A full-screen viewer opened again (pmslider makes a new one each time): with the manifest already
  // here, start it on the chosen size's GLB rather than loading pmslider's model first.
  function earlySrc(mv) {
    if (!manifest || !product || !mv.hasAttribute('ar')) return;
    var variant = selectedVariant(), size = variant && arSize(variant), entry = size && manifest.sizes[size];
    if (!entry || mv.getAttribute('src') === entry.glb) return;
    mv.dataset.pdOrigSrc = mv.getAttribute('src') || '';
    mv.dataset.pdArSrc = entry.glb;
    mv.setAttribute('src', entry.glb);
  }

  function apply(mv, variant) {
    var names = variantsOf(mv);
    if (!names) {             // a book, or another model now in this viewer: undo only our own changes
      if (mv.dataset.pdVariant) { mv.variantName = null; delete mv.dataset.pdVariant; setShadow(mv, null); }
      if (!mv.dataset.pdArSrc) clearAR(mv);       // (while our own AR src loads, it has no variants yet)
      return;
    }
    var name = wantedName(names, variant);
    if (mv.variantName !== name) mv.variantName = name;
    if (name) mv.dataset.pdVariant = name; else delete mv.dataset.pdVariant;
    setShadow(mv, name);
    applyAR(mv, variant, name || (names.indexOf('black') !== -1 ? 'black' : null));
  }

  function applyAll(force) {
    var variant = selectedVariant();
    if (!variant) return;
    if (!force && String(variant.id) === selectedId) return;
    selectedId = String(variant.id);
    document.querySelectorAll('model-viewer').forEach(function (mv) { apply(mv, variant); });
  }

  var queued = false;
  function refresh() {        // after the picker has updated the form and the URL
    if (!product || queued) return;
    queued = true;
    setTimeout(function () { queued = false; applyAll(false); }, 0);
  }

  function onModelLoad(mv) {
    if (!variantsOf(mv)) { if (mv.dataset.pdVariant) apply(mv, null); return; }
    loadProduct().then(function (p) {
      if (!p) return;
      var variant = selectedVariant();
      if (variant) { selectedId = String(variant.id); apply(mv, variant); }
      if (!polling) { polling = true; setInterval(function () { applyAll(false); }, 1000); }
    });
  }

  function watch(mv) {
    if (!mv || mv.dataset.pdVariants) return;
    mv.dataset.pdVariants = '1';
    earlySrc(mv);
    mv.addEventListener('load', function () { onModelLoad(mv); });   // every src, including pmslider's late ones
    if (mv.loaded) onModelLoad(mv);
  }

  function scan(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.tagName === 'MODEL-VIEWER') watch(node);
    else if (node.querySelectorAll) node.querySelectorAll('model-viewer').forEach(watch);
  }

  window.addEventListener('gp:product-change-variant', function (e) {   // GemPages product pages
    var id = e.detail && e.detail.variantSelected && e.detail.variantSelected.id;
    var variant = findVariant(id);
    if (!variant) { refresh(); return; }
    selectedId = String(variant.id);
    document.querySelectorAll('model-viewer').forEach(function (mv) { apply(mv, variant); });
  });
  document.addEventListener('change', refresh, true);
  window.addEventListener('popstate', refresh);

  new MutationObserver(function (records) {
    records.forEach(function (r) { r.addedNodes.forEach(scan); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  scan(document.documentElement);
})();
